import strings from "translate/language";
import { isFollowupClosingLike } from "./appointmentListStatusDisplay";

export function isLeadCreateStatus(item) {
  return /lead\s*create/i.test(String(item?.followup_status || ""));
}

/**
 * Walk-in once from API / Lead Create / oldest empty Call (CM/SCM Add Visitor).
 * GRE’s first follow-up keeps its own remark — do not steal appointment text onto it.
 */
export function resolveWalkinRemark(response, rows = []) {
  const fromApi = String(response?.walkin_remark || "").trim();
  if (fromApi) return fromApi;

  const list = Array.isArray(rows) ? rows : [];
  for (const row of list) {
    const fromRow = String(row?.walkin_remark || "").trim();
    if (fromRow) return fromRow;
  }

  // Newest → oldest in list; scan oldest-first for empty Call / Lead Create
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const row = list[i];
    if (
      !isLeadCreateStatus(row) &&
      !isFollowupClosingLike(row?.followup_status)
    ) {
      continue;
    }
    if (String(row?.remark || "").trim()) continue;
    const appt = String(row?.appointment_remark || "").trim();
    if (appt) return appt;
  }
  return "";
}

/**
 * Follow-up history sidebar: Call / Next Day Call / WhatsApp / Follow-Up only.
 * Lead Create is included when it is the only create-time row (no Call yet) so
 * CM/SCM walk-in still appears as the first follow-up.
 */
export function filterFollowupHistoryRows(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  const followups = list.filter((item) =>
    isFollowupClosingLike(item?.followup_status),
  );
  if (followups.length > 0) return followups;

  return list.filter((item) => {
    if (!isLeadCreateStatus(item)) return false;
    return !!(
      String(item?.walkin_remark || "").trim() ||
      String(item?.remark || "").trim() ||
      String(item?.appointment_remark || "").trim()
    );
  });
}

/**
 * First follow-up card:
 * - CM/SCM / add-visitor: auto Call (or Lead Create) with empty remark → walk-in
 * - GRE normal flow: their follow-up already has remark (no walk-in overlay)
 * Lists are newest → oldest.
 */
function isFirstFollowupHistoryRow(item, historyRows = []) {
  if (!item) return false;
  const candidates = (Array.isArray(historyRows) ? historyRows : []).filter(
    (row) =>
      isFollowupClosingLike(row?.followup_status) || isLeadCreateStatus(row),
  );
  if (!candidates.length) return false;
  const first = candidates[candidates.length - 1];
  if (item._id != null && first?._id != null) {
    return String(item._id) === String(first._id);
  }
  return item === first;
}

export function shouldShowWalkinOnHistoryRow(
  item,
  walkinRemark = "",
  historyRows = [],
) {
  const walkin = String(
    walkinRemark || item?.walkin_remark || "",
  ).trim();
  if (!walkin) return false;
  if (String(item?.remark || "").trim()) return false;
  if (isLeadCreateStatus(item)) return true;
  return isFirstFollowupHistoryRow(item, historyRows);
}

/** Description = follow-up remark; first empty row / Lead Create → walk-in once. */
export function historyDescription(
  item,
  walkinRemark = "",
  historyRows = [],
  emptyValue = "—",
) {
  const remark = String(item?.remark || "").trim();
  if (remark) return remark;
  if (shouldShowWalkinOnHistoryRow(item, walkinRemark, historyRows)) {
    return (
      String(walkinRemark || item?.walkin_remark || "").trim() || emptyValue
    );
  }
  return emptyValue;
}

export function historyDescriptionLabel(
  item,
  walkinRemark = "",
  historyRows = [],
) {
  if (shouldShowWalkinOnHistoryRow(item, walkinRemark, historyRows)) {
    return strings.walkinRemark;
  }
  return strings.description;
}

const hasFollowupLostReason = (item) => {
  const reason = item?.resiontitle;
  if (Array.isArray(reason)) return reason.some((v) => String(v || "").trim());
  return Boolean(String(reason || "").trim());
};

/** Match disposition history: older NI/Cancel titles stay Follow-up. */
export function displayFollowupHistoryStatus(item, index = 0) {
  const title = String(item?.followup_status || "").trim();
  if (!title) return title;
  const n = title.toLowerCase().replace(/\s+/g, " ");
  const isClosing =
    /not interest/.test(n) || /not fit/.test(n) || /cancel/.test(n);
  if (!isClosing) return title;
  const isLatest = Number(index) === 0;
  if (isLatest && hasFollowupLostReason(item)) return title;
  return "Follow-up";
}
